/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import { Timestamp } from 'firebase/firestore';

import Project, { projectSchema } from './project.js';

export const customerProjectSchema = projectSchema.shape({
  filename: Yup.string().required('Filename is required for customer projects'),
  partId: Yup.string().required('Part ID (Upload ID) is required for customer projects'),
  adminLink: Yup.string().nullable(),
});

/**
 * @typedef {Yup.InferType<typeof customerProjectSchema>} CustomerProjectSchema
 * @typedef {BaseDocData & CustomerProjectSchema & { createdAt: Timestamp; }} CustomerProjectDocData
*/

/**
 * Class representing a customer project.
 * @class
 * @extends Project
 */
export default class CustomerProject extends Project {
  /** @type {CustomerProjectDocData['filename']} */ filename;
  /** @type {CustomerProjectDocData['partId']} */ partId;
  /** @type {CustomerProjectDocData['adminLink']} */ adminLink;
  /** For displaying old data @type {any} */ material;

  /**
   * Create a customer project.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  constructor({
    filename, partId, adminLink, material, ...props
  }) {
    const validatedData = customerProjectSchema.validateSync({
      filename, partId, adminLink, ...props,
    });

    super(validatedData);

    this.filename = validatedData.filename;
    this.partId = validatedData.partId;
    this.adminLink = validatedData.adminLink;
    this.material = material;
  }

  /**
   * Converts the {@link CustomerProject} instance to Firestore document data.
   * @returns {Omit<CustomerProjectDocData, 'id'>} {@link CustomerProjectDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const baseDoc = super.toFirestore({ serverTimestamp });
    return {
      ...baseDoc,
      isInternal: false,
      filename: this.filename,
      partId: this.partId,
      adminLink: this.adminLink || `${process.env.REACT_APP_ORIGIN}/part/${this.partId}`,
    };
  }

  /**
   * Creates a {@link CustomerProject} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns A {@link CustomerProject} instance.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  static fromFirestore(docData) {
    return new CustomerProject(docData);
  }
}
